import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Sparkles, Play, Check, X, Loader, RefreshCw, ExternalLink, ThumbsUp, Search,
} from 'lucide-react';
import { adminAPI } from '../services/api';

/**
 * Review queue for peak-moment candidates mined from YouTube comments.
 *
 * Mining is cheap (1 quota unit per video) but only ~30-40% of suggestions are
 * genuine peaks — the rest are viewers discussing lore or correcting details.
 * So this screen is built around fast visual verification: every candidate can
 * be played inline at its exact timestamp before you accept or discard it.
 */

const CATEGORIES = ['general', 'movies', 'tv-shows', 'anime', 'music-performance', 'korean-drama'];
const MOMENT_TYPES = ['shocking', 'emotional', 'funny', 'plot_twist', 'epic', 'wholesome', 'scary', 'dramatic', 'exciting', 'cringe'];

const AdminPeakCandidatesTab = ({ colors, showAlert }) => {
  const c = colors || {};
  const [stats, setStats] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [batchSize, setBatchSize] = useState(25);
  const [minLikes, setMinLikes] = useState(5);
  const [preview, setPreview] = useState(null);      // candidate being played
  const [busyId, setBusyId] = useState(null);
  const [edits, setEdits] = useState({});            // per-candidate title/category/type
  const [job, setJob] = useState(null);              // background scan progress
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, list] = await Promise.all([
        adminAPI.getPeakCandidateStats(),
        adminAPI.getPeakCandidates(status),
      ]);
      setStats(s.data?.stats || null);
      setCandidates(list.data?.candidates || []);
    } catch (e) {
      showAlert && showAlert({ type: 'error', message: 'Failed to load candidates' });
    } finally {
      setLoading(false);
    }
  }, [status, showAlert]);

  useEffect(() => { load(); }, [load]);

  // Watch a running scan. A batch is minutes long, so progress is polled rather
  // than awaited — the request that starts it returns straight away.
  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await adminAPI.getPeakScanStatus();
        const j = res.data?.job || null;
        setJob(j);
        if (j && !j.running) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setScanning(false);
          const s = j.summary || {};
          showAlert && showAlert({
            type: j.error ? 'error' : 'success',
            message: j.error
              ? `Scan stopped: ${j.error}`
              : `Scanned ${s.scanned} videos (${s.quotaUsed} quota units) — ${s.candidatesStored} new candidates, ${s.skipped} skipped.`,
          });
          load();
        }
      } catch {
        /* transient — keep polling */
      }
    }, 2000);
  }, [load, showAlert]);

  // Reattach to a scan already running when the tab mounts (e.g. after a reload)
  useEffect(() => {
    adminAPI.getPeakScanStatus()
      .then((res) => {
        const j = res.data?.job || null;
        setJob(j);
        if (j?.running) { setScanning(true); startPolling(); }
      })
      .catch(() => {});
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [startPolling]);

  const runScan = async () => {
    setScanning(true);
    setJob(null);
    try {
      const res = await adminAPI.scanPeakCandidates({ limit: batchSize, minLikes });
      setJob(res.data?.job || null);
      startPolling();
    } catch (e) {
      setScanning(false);
      const msg = e?.response?.status === 409
        ? 'A scan is already running.'
        : (e?.response?.data?.error || e.message || 'unknown error');
      showAlert && showAlert({ type: 'error', message: 'Scan failed: ' + msg });
    }
  };

  const approve = async (cand) => {
    setBusyId(cand._id);
    try {
      const e = edits[cand._id] || {};
      await adminAPI.approvePeakCandidate(cand._id, {
        title: e.title ?? cand.sampleComment?.slice(0, 100) ?? '',
        category: e.category || 'general',
        momentType: e.momentType || 'shocking',
      });
      setCandidates(prev => prev.filter(x => x._id !== cand._id));
      showAlert && showAlert({ type: 'success', message: 'Peak moment created' });
    } catch {
      showAlert && showAlert({ type: 'error', message: 'Approve failed' });
    } finally { setBusyId(null); }
  };

  const reject = async (cand) => {
    setBusyId(cand._id);
    try {
      await adminAPI.rejectPeakCandidate(cand._id);
      setCandidates(prev => prev.filter(x => x._id !== cand._id));
    } catch {
      showAlert && showAlert({ type: 'error', message: 'Reject failed' });
    } finally { setBusyId(null); }
  };

  const setEdit = (id, patch) => setEdits(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));

  const card = {
    background: c.bgSecondary || '#111827',
    border: `1px solid ${c.border || '#232130'}`,
    borderRadius: 12,
  };
  const label = { fontSize: 11, color: c.textSecondary || '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 };

  return (
    <div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <div style={{
          width: 42, height: 42, borderRadius: 11,
          background: 'linear-gradient(135deg,#F97316,#EAB308)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Sparkles size={21} color="#100c14" />
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: c.text || '#fff' }}>Peak Moment Candidates</h2>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: c.textSecondary || '#9CA3AF' }}>
            Mined from top YouTube comments. Play each one at its timestamp before approving.
          </p>
        </div>
        <button
          onClick={load}
          style={{ ...card, padding: '9px 14px', color: c.text || '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 600 }}
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, marginBottom: 18 }}>
          {[
            { k: 'Pending review', v: stats.pending, col: '#EAB308' },
            { k: 'Approved', v: stats.approved, col: '#22C55E' },
            { k: 'Rejected', v: stats.rejected, col: '#EF4444' },
            { k: 'Videos scanned', v: `${stats.scanned.toLocaleString()} / ${stats.totalVideos.toLocaleString()}`, col: '#8B5CF6' },
          ].map(s => (
            <div key={s.k} style={{ ...card, padding: 14 }}>
              <div style={label}>{s.k}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.col, marginTop: 6 }}>{s.v}</div>
            </div>
          ))}
        </div>
      )}

      {/* scan controls */}
      <div style={{ ...card, padding: 16, marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <div style={{ ...label, marginBottom: 5 }}>Videos to scan</div>
            <input
              type="number" min="1" max="100" value={batchSize}
              onChange={e => setBatchSize(Math.min(100, Math.max(1, parseInt(e.target.value, 10) || 1)))}
              style={{ width: 90, padding: '9px 10px', borderRadius: 8, border: `1px solid ${c.border || '#232130'}`, background: c.bgTertiary || '#1F2937', color: c.text || '#fff', fontSize: 14 }}
            />
          </div>
          <div>
            <div style={{ ...label, marginBottom: 5 }}>Min likes</div>
            <input
              type="number" min="0" value={minLikes}
              onChange={e => setMinLikes(Math.max(0, parseInt(e.target.value, 10) || 0))}
              style={{ width: 90, padding: '9px 10px', borderRadius: 8, border: `1px solid ${c.border || '#232130'}`, background: c.bgTertiary || '#1F2937', color: c.text || '#fff', fontSize: 14 }}
            />
          </div>
          <button
            onClick={runScan}
            disabled={scanning}
            style={{
              padding: '11px 20px', borderRadius: 9, border: 'none',
              background: scanning ? '#4B5563' : 'linear-gradient(135deg,#F97316,#EAB308)',
              color: scanning ? '#fff' : '#100c14', fontWeight: 800, fontSize: 14,
              cursor: scanning ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            {scanning ? <><Loader size={15} style={{ animation: 'spin 1s linear infinite' }} /> Scanning…</> : <><Search size={15} /> Scan for candidates</>}
          </button>
          <div style={{ fontSize: 12.5, color: c.textSecondary || '#9CA3AF', paddingBottom: 10 }}>
            Costs <strong style={{ color: '#EAB308' }}>{batchSize} quota unit{batchSize === 1 ? '' : 's'}</strong> (1 per video).
            {stats ? ` ${stats.unscanned.toLocaleString()} videos still unscanned.` : ''}
            <br />Roughly {Math.max(1, Math.round(batchSize * 1.1 / 60))} min — runs in the background, you can leave this tab.
          </div>
        </div>

        {/* live progress of the background job */}
        {job && (job.running || job.error) && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${c.border || '#232130'}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: c.textSecondary || '#9CA3AF', marginBottom: 7 }}>
              <span>
                {job.running ? 'Scanning' : 'Stopped'} — {job.summary?.scanned || 0} / {job.summary?.total || job.requested} videos
              </span>
              <span>
                {job.summary?.candidatesStored || 0} candidates found
                {job.summary?.skipped ? ` · ${job.summary.skipped} skipped` : ''}
              </span>
            </div>
            <div style={{ height: 6, borderRadius: 999, background: c.bgTertiary || '#1F2937', overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${Math.round(((job.summary?.scanned || 0) / Math.max(1, job.summary?.total || job.requested)) * 100)}%`,
                background: 'linear-gradient(90deg,#F97316,#EAB308)',
                transition: 'width 0.4s ease',
              }} />
            </div>
          </div>
        )}
      </div>

      {/* status filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {['pending', 'approved', 'rejected'].map(s => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            style={{
              padding: '8px 16px', borderRadius: 999,
              border: status === s ? 'none' : `1px solid ${c.border || '#232130'}`,
              background: status === s ? 'linear-gradient(135deg,#8b5cf6,#ec4899)' : 'transparent',
              color: status === s ? '#fff' : (c.textSecondary || '#9CA3AF'),
              fontWeight: status === s ? 700 : 600, fontSize: 13, cursor: 'pointer', textTransform: 'capitalize',
            }}
          >
            {s}
          </button>
        ))}
      </div>

      {/* list */}
      {loading ? (
        <div style={{ padding: 50, textAlign: 'center' }}>
          <Loader size={26} style={{ color: '#8B5CF6', animation: 'spin 1s linear infinite' }} />
        </div>
      ) : candidates.length === 0 ? (
        <div style={{ ...card, padding: 40, textAlign: 'center', color: c.textSecondary || '#9CA3AF' }}>
          No {status} candidates. {status === 'pending' && 'Run a scan to generate some.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {candidates.map(cand => {
            const e = edits[cand._id] || {};
            const isOpen = preview === cand._id;
            const startAt = Math.max(0, cand.timestampSeconds - 3);
            return (
              <div key={cand._id} style={{ ...card, padding: 14 }}>
                <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {/* thumbnail -> opens inline player */}
                  <button
                    onClick={() => setPreview(isOpen ? null : cand._id)}
                    title="Play at this timestamp"
                    style={{
                      position: 'relative', width: 200, aspectRatio: '16/9', flexShrink: 0,
                      border: 'none', padding: 0, borderRadius: 9, overflow: 'hidden',
                      cursor: 'pointer', background: '#000',
                    }}
                  >
                    {cand.videoThumbnail && (
                      <img src={cand.videoThumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    )}
                    <span style={{
                      position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <span style={{
                        width: 42, height: 42, borderRadius: '50%',
                        background: 'linear-gradient(135deg,#F97316,#EAB308)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Play size={18} fill="#100c14" color="#100c14" style={{ marginLeft: 2 }} />
                      </span>
                    </span>
                    <span style={{
                      position: 'absolute', bottom: 6, right: 6, background: 'rgba(0,0,0,0.85)',
                      color: '#fff', fontSize: 12, fontWeight: 700, padding: '2px 7px', borderRadius: 5,
                    }}>
                      {cand.timestampFormatted}
                    </span>
                  </button>

                  <div style={{ flex: 1, minWidth: 240 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: c.text || '#fff' }}>{cand.channelName}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#EAB308', fontWeight: 700 }}>
                        <ThumbsUp size={12} /> {cand.likeCount.toLocaleString()}
                      </span>
                      {cand.mentionCount > 1 && (
                        <span style={{ fontSize: 11, color: c.textSecondary || '#9CA3AF' }}>
                          {cand.mentionCount} comments
                        </span>
                      )}
                      <a
                        href={`https://youtu.be/${cand.videoId}?t=${startAt}`}
                        target="_blank" rel="noreferrer"
                        style={{ fontSize: 12, color: '#c4b5fd', display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}
                      >
                        <ExternalLink size={12} /> YouTube
                      </a>
                    </div>

                    <p style={{ margin: '0 0 6px', fontSize: 12.5, color: c.textSecondary || '#9CA3AF' }}>
                      {cand.videoTitle}
                    </p>

                    <p style={{
                      margin: '0 0 10px', fontSize: 13, color: c.text || '#fff',
                      fontStyle: 'italic', background: c.bgTertiary || '#1F2937',
                      padding: '8px 10px', borderRadius: 7, lineHeight: 1.45,
                    }}>
                      “{cand.sampleComment}”
                    </p>

                    {status === 'pending' && (
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <input
                          placeholder="Moment title"
                          maxLength={100}
                          value={e.title ?? (cand.sampleComment || '').slice(0, 100)}
                          onChange={ev => setEdit(cand._id, { title: ev.target.value })}
                          style={{ flex: 1, minWidth: 200, padding: '8px 10px', borderRadius: 7, border: `1px solid ${c.border || '#232130'}`, background: c.bgTertiary || '#1F2937', color: c.text || '#fff', fontSize: 13 }}
                        />
                        <select
                          value={e.category || 'general'}
                          onChange={ev => setEdit(cand._id, { category: ev.target.value })}
                          style={{ padding: '8px 10px', borderRadius: 7, border: `1px solid ${c.border || '#232130'}`, background: c.bgTertiary || '#1F2937', color: c.text || '#fff', fontSize: 13 }}
                        >
                          {CATEGORIES.map(x => <option key={x} value={x}>{x}</option>)}
                        </select>
                        <select
                          value={e.momentType || 'shocking'}
                          onChange={ev => setEdit(cand._id, { momentType: ev.target.value })}
                          style={{ padding: '8px 10px', borderRadius: 7, border: `1px solid ${c.border || '#232130'}`, background: c.bgTertiary || '#1F2937', color: c.text || '#fff', fontSize: 13 }}
                        >
                          {MOMENT_TYPES.map(x => <option key={x} value={x}>{x}</option>)}
                        </select>
                        <button
                          onClick={() => approve(cand)}
                          disabled={busyId === cand._id}
                          style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: '#22C55E', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                        >
                          <Check size={14} /> Approve
                        </button>
                        <button
                          onClick={() => reject(cand)}
                          disabled={busyId === cand._id}
                          style={{ padding: '9px 14px', borderRadius: 8, border: `1px solid ${c.border || '#232130'}`, background: 'transparent', color: '#EF4444', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                        >
                          <X size={14} /> Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* inline player, starts a few seconds before the peak */}
                {isOpen && (
                  <div style={{ marginTop: 12, borderRadius: 9, overflow: 'hidden', background: '#000', aspectRatio: '16/9', maxWidth: 640 }}>
                    <iframe
                      title={cand.videoId + cand.timestampSeconds}
                      src={`https://www.youtube-nocookie.com/embed/${cand.videoId}?start=${startAt}&autoplay=1&rel=0&playsinline=1`}
                      allow="autoplay; encrypted-media; picture-in-picture"
                      allowFullScreen
                      style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminPeakCandidatesTab;
